/**
 * The one default for a new scene's stored `content` (spec 004). The Service
 * seeds `POST /scenes` from it and the Editor uses its background when a
 * stored scene has none. Shaped by the Shared types; never rewritten into an
 * existing scene except when the author saves.
 */
import type { Background, DerivedPart, OrientationLayout, PlaybackSettings } from '../@types';
import { isDerived, withDerived } from './responsiveLayout';

export const DEFAULT_BACKGROUND_COLOR = '#1a1a2e';

/** A Shared `Background` with no image and no layers. */
export const createDefaultBackground = (): Background => ({
  id: 'background',
  name: 'Background',
  color: DEFAULT_BACKGROUND_COLOR,
  layers: [],
});

/**
 * The one playback default (spec 007): Service seeding and reads, publish's
 * fallback and the Editor all use this object.
 */
export const DEFAULT_PLAYBACK_SETTINGS: PlaybackSettings = {
  autoPlay: false,
  allowSkip: true,
  allowPause: true,
  defaultBgmVolume: 0.5,
  defaultSfxVolume: 1.0,
  defaultVoiceVolume: 1.0,
  muteOnInactive: true,
  showControls: true,
  showProgress: true,
  fullscreenEnabled: true,
  textSpeed: 'normal',
  autoAdvanceDialog: false,
  autoAdvanceDelay: 2000,
};

export interface DefaultSceneContent {
  background: Background;
  sprites: [];
  dialogs: [];
  sounds: [];
  signals: [];
  keyframes: [];
  choices: null;
  choiceSettings: null;
  duration: number;
  playbackSettings: PlaybackSettings;
}

/**
 * A new scene follows its content (`duration: 0`, spec 041 D-7: it plays as
 * long as its content, at least 1 s) and plays its authored keyframes (none
 * yet); it has no composer mode (spec 006 v5).
 */
export const createDefaultSceneContent = (): DefaultSceneContent => ({
  background: createDefaultBackground(),
  sprites: [],
  dialogs: [],
  sounds: [],
  signals: [],
  keyframes: [],
  choices: null,
  choiceSettings: null,
  duration: 0,
  playbackSettings: { ...DEFAULT_PLAYBACK_SETTINGS },
});

/** The storyboard sprite fields Storyboard left on sprites and `sprite.layout[o]` (spec 006 v5). */
const RETIRED_SPRITE_FIELDS = ['enterAnimation', 'exitAnimation', 'delay', 'hold'] as const;
const LAYOUT_ORIENTATIONS = ['portrait', 'landscape', 'desktop'] as const;

const hasAny = (o: Record<string, unknown>) => RETIRED_SPRITE_FIELDS.some((f) => f in o);

/**
 * Whether any sprite (or any `sprite.layout[o]`) still carries a storyboard
 * field (`enterAnimation`, `exitAnimation`, `delay`, `hold`). The one-off
 * migration converts a Storyboard scene only while this holds, and
 * `PATCH /scenes/:id` drops the stored mode field once it no longer does
 * (spec 006 v7, 004 v7): a scene saved by the editor after Storyboard was
 * retired has neither, and its keyframes are the author's.
 */
export function hasStoryboardSpriteFields(sprites: unknown): boolean {
  if (!Array.isArray(sprites)) return false;
  return sprites.some((sprite) => {
    if (!sprite || typeof sprite !== 'object') return false;
    const s = sprite as Record<string, unknown>;
    if (hasAny(s)) return true;
    const layout = s.layout && typeof s.layout === 'object' ? (s.layout as Record<string, unknown>) : undefined;
    return !!layout && LAYOUT_ORIENTATIONS.some((o) => {
      const e = layout[o];
      return !!e && typeof e === 'object' && hasAny(e as Record<string, unknown>);
    });
  });
}

/**
 * The content without the stored mode field Storyboard left once no sprite
 * carries a storyboard field (it then governs nothing), else the same
 * object: while one does, the mode stays as the one-off migration's marker.
 * Pure; what `PATCH /scenes/:id` writes goes through it (spec 004 v7). The
 * field itself is named only in `stripRetiredSceneFields` (spec 037).
 */
export function dropStaleComposerMode<T extends object>(content: T): T {
  if (hasStoryboardSpriteFields((content as { sprites?: unknown }).sprites)) return content;
  return stripRetiredSceneFields(content);
}

/** A layout entry without the retired fields and their parts of the derived mark (spec 021). */
function stripLayoutEntry(entry: Record<string, unknown>): Record<string, unknown> {
  let next = entry as Partial<OrientationLayout>;
  for (const f of RETIRED_SPRITE_FIELDS) next = withDerived(next, f as DerivedPart, false);
  const out = { ...(next as Record<string, unknown>) };
  for (const f of RETIRED_SPRITE_FIELDS) delete out[f];
  return out;
}

function stripSprite(sprite: unknown): unknown {
  if (!sprite || typeof sprite !== 'object') return sprite;
  const s = sprite as Record<string, unknown>;
  const layout = s.layout && typeof s.layout === 'object' ? (s.layout as Record<string, unknown>) : undefined;
  const dirtyLayout = !!layout && LAYOUT_ORIENTATIONS.some((o) => {
    const e = layout[o];
    return !!e && typeof e === 'object' && (hasAny(e as Record<string, unknown>) || RETIRED_SPRITE_FIELDS.some((f) => isDerived(e as Partial<OrientationLayout>, f as DerivedPart)));
  });
  if (!hasAny(s) && !dirtyLayout) return sprite;
  const out: Record<string, unknown> = { ...s };
  for (const f of RETIRED_SPRITE_FIELDS) delete out[f];
  if (dirtyLayout) {
    const nextLayout: Record<string, unknown> = { ...layout };
    for (const o of LAYOUT_ORIENTATIONS) {
      const e = nextLayout[o];
      if (e && typeof e === 'object') nextLayout[o] = stripLayoutEntry(e as Record<string, unknown>);
    }
    out.layout = nextLayout;
  }
  return out;
}

/**
 * The one place that names what Storyboard left on a scene (spec 006 v5).
 * Pure: returns the content without the mode field and without
 * `enterAnimation`, `exitAnimation`, `delay` and `hold` on every sprite and
 * every `sprite.layout[o]` (with their parts of the derived mark), or the
 * same object when there is nothing to strip. The store's `loadScene` and
 * spec 012's `toPlayableScene` call it; the one-off migration
 * (`npm run scenes:retire-storyboard`) converted every stored scene's motion
 * before this strip could lose it.
 */
export function stripRetiredSceneFields<T extends object>(content: T): T {
  const c = content as T & { composerMode?: unknown; sprites?: unknown };
  const sprites = Array.isArray(c.sprites) ? (c.sprites as unknown[]) : undefined;
  const nextSprites = sprites?.map(stripSprite);
  const spritesChanged = !!sprites && nextSprites!.some((s, i) => s !== sprites[i]);
  if (!('composerMode' in c) && !spritesChanged) return content;
  const { composerMode: _retired, ...rest } = c;
  return (spritesChanged ? { ...rest, sprites: nextSprites } : rest) as T;
}
